import schema from '@aether/client-graphql/generated/genai-introspection.json';
import {
  type ChatPromptFeedbackInput,
  type ChatPromptInput,
  type CreateChatInput,
  type NewChatPromptFragment,
  type NewChatPromptMutation,
  type NewCreateChatMutation,
  type UpdateChatInput,
} from '@aether/client-graphql/generated/graphql';
import { cacheExchange } from '@urql/exchange-graphcache';
import { persistedExchange } from '@urql/exchange-persisted';
import {
  createClient,
  fetchExchange,
  gql,
  Provider,
  useMutation,
  useQuery,
  type Client,
  type TypedDocumentNode,
} from 'urql';

import { GRAPHQL_ENDPOINT } from '@/env';

import { chatQuery, type ChatWithPromptsFragment } from './gql';

let client: Client;

const TEMP_CHAT_ID = 'temp-chat-id';
const TEMP_PROMPT_ID = 'temp-prompt-id';

function getClient() {
  if (!client) {
    client = createClient({
      url: GRAPHQL_ENDPOINT,
      fetchOptions: {
        credentials: 'include',
      },
      exchanges: [
        cacheExchange({
          schema,
          resolvers: {
            Query: {
              chat: (_, args, cache) => {
                if (args.id === TEMP_CHAT_ID) {
                  // race condition fix:
                  // when the createChat mutation returns, the optimistically created chat is removed from the cache.
                  // There is a useQuery hook still using the temp-chat-id, as the update to the actual id happens on the next render
                  // Because the entry is not in cache, the hook tries to fetch it from the server instead.
                  // To avoid this we explicitly return `null` in the id resolver if the temp chat is not in cache.
                  const isTempInCache = cache.resolve({ __typename: 'Chat', id: TEMP_CHAT_ID }, 'id');
                  if (isTempInCache) {
                    return { __typename: 'Chat', id: TEMP_CHAT_ID };
                  }
                  return null;
                }
                return { __typename: 'Chat', id: args.id };
              },
            },
          },
          updates: {
            Mutation: {
              chatPrompt: (result: NewChatPromptMutation, _args, cache) => {
                const chatId = result.chatPrompt.chatId!;
                const prompts = cache.resolve({ __typename: 'Chat', id: chatId }, 'prompts') || [];
                if (Array.isArray(prompts)) {
                  cache.link({ __typename: 'Chat', id: chatId }, 'prompts', [...prompts, result.chatPrompt]);
                } else {
                  console.error('prompts is not an array', prompts);
                }

                const contexts = cache.resolve({ __typename: 'Chat', id: chatId }, 'contexts') || [];
                const newContexts = result.chatPrompt.contexts;
                if (Array.isArray(contexts) && newContexts.length > 0) {
                  cache.link({ __typename: 'Chat', id: chatId }, 'contexts', [...contexts, ...newContexts]);
                }
              },
              createChat: (result: NewCreateChatMutation, _args, cache) => {
                const chats = cache.resolve({ __typename: 'ChatList', id: 'chat-list' }, 'data') || [];
                if (Array.isArray(chats)) {
                  cache.link({ __typename: 'ChatList', id: 'chat-list' }, 'data', [result.createChat, ...chats]);
                } else {
                  console.error('chats is not an array', chats);
                }
              },
              deleteChat: (_result, args: { id: string }, cache) => {
                cache.invalidate({ __typename: 'Chat', id: args.id });
              },
              chatPromptFeedback: (_result, args: { input: ChatPromptFeedbackInput }, cache) => {
                const fragment = gql`
                  fragment _ on ChatPrompt {
                    id
                    feedback
                  }
                `;
                cache.writeFragment(fragment, {
                  id: args.input.promptId,
                  feedback: args.input.feedback,
                });
              },
            },
          },
          optimistic: {
            updateChat(args: { input: UpdateChatInput }) {
              return {
                __typename: 'Chat',
                id: args.input.id,
                name: args.input.name,
              };
            },
            chatPrompt: (args: { input: ChatPromptInput }) => {
              const prompt = {
                __typename: 'ChatPrompt',
                id: TEMP_PROMPT_ID,
                chatId: args.input.chatId,
                dateCreated: new Date().toISOString(),
                prompt: args.input.prompt,
                response: '',
                promptTokenCount: 0,
                responseTokenCount: 0,
                duration: 0,
                contexts:
                  args.input.contexts?.map(context => ({
                    __typename: 'ChatContext',
                    id: JSON.stringify(context),
                    ...context,
                  })) || [],
              } satisfies NewChatPromptFragment;
              return prompt;
            },
            createChat: (args: { input: CreateChatInput }) => {
              const newChat = {
                __typename: 'Chat',
                id: TEMP_CHAT_ID,
                name: args.input.name || 'Untitled chat',
                dateCreated: new Date().toISOString(),
                dateUpdated: new Date().toISOString(),
                version: '1.0.0',
                contexts:
                  args.input.contexts?.map(context => ({
                    __typename: 'ChatContext',
                    id: JSON.stringify(context),
                    ...context,
                  })) || [],
                prompts: args.input.prompt
                  ? [
                      {
                        __typename: 'ChatPrompt',
                        id: TEMP_PROMPT_ID,
                        chatId: TEMP_CHAT_ID,
                        dateCreated: new Date().toISOString(),
                        prompt: args.input.prompt,
                        response: '',
                        promptTokenCount: 0,
                        responseTokenCount: 0,
                        duration: 0,
                        contexts:
                          args.input.contexts?.map(context => ({
                            __typename: 'ChatContext',
                            id: JSON.stringify(context),
                            ...context,
                          })) || [],
                      },
                    ]
                  : [],
              } satisfies ChatWithPromptsFragment;
              return newChat;
            },
            chatPromptFeedback: (args: { input: ChatPromptFeedbackInput }) => {
              return {
                __typename: 'ChatPrompt',
                id: args.input.promptId,
                feedback: args.input.feedback,
              };
            },
          },
        }),
        persistedExchange({
          enforcePersistedQueries: true,
          enableForMutation: true,
          generateHash: async (_, document: TypedDocumentNode & { __meta__?: { hash: string } }) =>
            document.__meta__?.hash || '',
        }),
        fetchExchange,
      ],
    });
  }
  return client;
}

function useChat(id?: string | null) {
  return useQuery({
    query: chatQuery,
    variables: { id: id || '' },
    pause: typeof id !== 'string',
    requestPolicy: id === TEMP_CHAT_ID ? 'cache-only' : 'cache-and-network',
  });
}

function QueryProvider({ children }: { children: React.ReactNode }) {
  return <Provider value={getClient()}>{children}</Provider>;
}

export { useQuery, useChat, useMutation, QueryProvider };
